import { useState } from 'react'

import Bag3Icon from '../../../../../../../assets/svgs/Bag3'
import HatIcon from '../../../../../../../assets/svgs/Hat'

import { compactNumber } from '../../../../format'

import CourseItem from './CourseItem'
import PositionItem from './PositionItem'
import SeeAllOnChimp from '../SeeAllOnChimp'
import style from './style.module.css'

const SKELETON_ROWS = 3

/**
 * The third column of the modal dashboard.
 *
 * It shows open roles, but a lot of organisations in this data are not employers
 * at all — Coursera has 3,300 courses and no jobs, universities have both. A
 * fixed "Positions" panel would render an empty box for every one of them, so
 * the panel adapts: roles when there are roles, courses when there are only
 * courses, and a small tab switcher when the organisation has both.
 */
const PositionsBlock = ({
  company,
  positions = [],
  courses = [],
  loading = false,
  error = null,
  onRetry,
}) => {
  const positionList = Array.isArray(positions) ? positions : []
  const courseList = Array.isArray(courses) ? courses : []

  const hasPositions = positionList.length > 0
  const hasCourses = courseList.length > 0
  const hasBoth = hasPositions && hasCourses

  // Roles lead when present; the tab only exists to reach the other list.
  const [tab, setTab] = useState('positions')
  const activeTab = hasBoth ? tab : hasPositions ? 'positions' : hasCourses ? 'courses' : 'positions'

  const showingCourses = activeTab === 'courses'
  const list = showingCourses ? courseList : positionList

  const totalRaw = showingCourses ? company?.counts?.courses : company?.counts?.openJobs
  const total = Number(totalRaw)
  const hasTotal = Number.isFinite(total) && total > 0

  // The detail endpoint returns a sample, not everything, so say how many are
  // left rather than implying the list is complete. There is no per-company
  // listing route to link to, so this stays plain text.
  const remaining = hasTotal ? total - list.length : 0
  const showRemaining = !loading && !error && list.length > 0 && remaining > 0

  const isEmpty = !loading && !error && list.length === 0

  const Icon = showingCourses ? HatIcon : Bag3Icon
  const heading = showingCourses ? 'Courses' : 'Positions'
  const emptyMessage = showingCourses ? 'No courses listed' : 'No open roles right now'

  return (
    <div className={style.companyModal_positions}>
      {hasBoth ? (
        <div className={style.companyModal_position_tabs} role="tablist" aria-label="Roles and courses">
          <button
            type="button"
            role="tab"
            aria-selected={!showingCourses}
            className={`${style.companyModal_position_tab} ${
              !showingCourses ? style.companyModal_position_tab_active : ''
            }`}
            onClick={() => setTab('positions')}
            data-log-action="spotlight_tab_positions"
          >
            Roles
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={showingCourses}
            className={`${style.companyModal_position_tab} ${
              showingCourses ? style.companyModal_position_tab_active : ''
            }`}
            onClick={() => setTab('courses')}
            data-log-action="spotlight_tab_courses"
          >
            Learn
          </button>
        </div>
      ) : (
        <div
          className={`${style.companyModal_positions_title} ${
            showingCourses ? style.companyModal_positions_title_course : ''
          }`}
        >
          {/* HatIcon ships at 60x60 with a white stroke (it was drawn for a dark
              surface), so the course variant is resized and recoloured in CSS —
              these icon components ignore props entirely. */}
          <Icon />
          <span>
            {heading}
            {hasTotal ? ` (${compactNumber(total)})` : ''}
          </span>
        </div>
      )}

      <div className={style.companyModal_position_list} aria-busy={loading}>
        {loading &&
          Array.from({ length: SKELETON_ROWS }).map((item, index) => (
            <div
              // Skeletons carry no identity of their own, so the index is the key.
              key={`position-skeleton-${index}`}
              className={style.companyModal_position_skeleton}
              aria-hidden="true"
            >
              <span />
              <span />
            </div>
          ))}

        {!loading && error && (
          <div className={style.companyModal_position_message} role="status">
            <span>Could not load roles.</span>
            <button
              type="button"
              className={style.companyModal_position_retry}
              onClick={onRetry}
              data-log-action="spotlight_retry_company"
            >
              Retry
            </button>
          </div>
        )}

        {isEmpty && (
          <div className={style.companyModal_position_message}>
            <span>{emptyMessage}</span>
          </div>
        )}

        {!loading &&
          !error &&
          (showingCourses
            ? courseList.map((course) => <CourseItem key={course.id} course={course} />)
            : positionList.map((position) => (
                <PositionItem key={position.id} position={position} />
              )))}
      </div>

      {!loading && !error && list.length > 0 && (
        <div className={style.companyModal_position_showMore}>
          {showRemaining && (
            <span className={style.companyModal_position_remaining}>
              {compactNumber(remaining)} more
            </span>
          )}
          <SeeAllOnChimp
            companyName={company?.name}
            label={showingCourses ? 'See all on Chimp' : 'See all roles on Chimp'}
          />
        </div>
      )}
    </div>
  )
}

export default PositionsBlock
